import { useMemo, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { OrderSerial } from "models/OrderSerial";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useEditOrderSerialMutation } from "app/api/checklists";
import { useOrderContext } from "context/OrderContext";
import LargeDialog from "components/LargeDialog";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";

type Props = {
  serial: OrderSerial;
  onClose?: () => void;
};

const EditOrderSerialDialog: React.FC<Props> = ({ onClose, serial }) => {
  const order = useOrderContext();
  const { hasRole } = useAuthContext();
  const canEditSerial = hasRole([
    UserRole.ADMIN,
    UserRole.USER_ADMIN,
    UserRole.SALES,
    UserRole.INTERNAL_INSTALLER,
    UserRole.EXTERNAL_INSTALLER,
  ]);
  const [editSerial, { isLoading: isEditing }] = useEditOrderSerialMutation();

  // Find checklist this product belongs to, if any
  const checklist = useMemo(
    () => order.checklists?.find((c) => c.roomName === serial.room),
    [order, serial]
  );

  const [showAllFields, setShowAllFields] = useState<boolean>(false);

  // Variables for new serial form
  const [serialNumber, setSerialNumber] = useState<string>(serial.serial || "");
  const [description, setDescription] = useState<string>(serial.description || "");
  const [macAddressWired, setMacAddressWired] = useState<string>(serial.macAddressWired || "");
  const [ethernetPort, setEthernetPort] = useState<string>(serial.ethernetPort || "");
  const [theftMarking, setTheftMarking] = useState<string>(serial.theftMarking || "");
  const [macAddressWifi, setMacAddressWifi] = useState<string>(serial.macAddressWifi || "");
  const [ipAddress, setIpAddress] = useState<string>(serial.ip || "");
  const [username, setUsername] = useState<string>(serial.userName || "");
  const [password, setPassword] = useState<string>(serial.password || "");
  const [assetNumber, setAssetNumber] = useState<string>(serial.assetNo || "");
  const [firmware, setFirmware] = useState<string>(serial.firmware || "");
  const [other, setOther] = useState<string>(serial.other || "");
  const [doa, setDoa] = useState<boolean>(serial.doa || false);

  const isNoFieldEmpty = Boolean(
    macAddressWired &&
    ethernetPort &&
    theftMarking &&
    macAddressWifi &&
    ipAddress &&
    username &&
    password &&
    assetNumber &&
    firmware &&
    other
  );

  const handleEditSerial = async () => {
    editSerial({
      orderId: order.ordernr,
      checklistId: checklist?.id,
      id: serial?.id || "",
      serial: serialNumber,
      // only child serials can edit description
      description: serial.parentId != null ? description : "",
      macAddressWired: macAddressWired,
      macAddressWifi: macAddressWifi,
      ethernetPort: ethernetPort,
      theftMarking: theftMarking,
      ip: ipAddress,
      userName: username,
      password: password,
      firmware: firmware,
      assetNo: assetNumber,
      other: other,
      doa: doa,
    })
      .unwrap()
      .then(() => {
        toast.success("Produkten har redigerats!", {
          autoClose: 3000,
          hideProgressBar: true,
        });

        onClose?.();
      })
      .catch(() => {
        toast.error("Något gick fel när produkten skulle redigeras", {
          autoClose: 3000,
          hideProgressBar: true,
        });
      });
  };

  return (
    <LargeDialog
      onClose={() => onClose?.()}
      title="Redigera produkt"
      onSubmit={(e) => {
        e.preventDefault();
        handleEditSerial();
      }}
      actions={
        <>
          <Button
            variant="outlined"
            disabled={isEditing}
            startIcon={<CloseIcon />}
            onClick={onClose}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            loading={isEditing}
          >
            Spara
          </Button>
        </>
      }
    >
      <Box>
        <Typography fontWeight="bold">{serial.item}</Typography>
        <Typography>{serial.description}</Typography>
      </Box>

      {/* Only child serials can edit description */}
      {serial.parentId !== null && (
        <TextField
          variant="filled"
          label="Benämning"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      )}
      <TextField
        variant="filled"
        label="Serienummer"
        disabled={!canEditSerial}
        fullWidth
        required
        value={serialNumber}
        onChange={(e) => setSerialNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress"
        fullWidth
        hidden={!showAllFields && !macAddressWired}
        value={macAddressWired}
        onChange={(e) => setMacAddressWired(e.target.value)}
      />
      <TextField
        variant="filled"
        label="MAC-adress Wi-Fi"
        fullWidth
        hidden={!showAllFields && !macAddressWifi}
        value={macAddressWifi}
        onChange={(e) => setMacAddressWifi(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Nätverksuttag"
        fullWidth
        hidden={!showAllFields && !ethernetPort}
        value={ethernetPort}
        onChange={(e) => setEthernetPort(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Stöldmärkning"
        fullWidth
        hidden={!showAllFields && !theftMarking}
        value={theftMarking}
        onChange={(e) => setTheftMarking(e.target.value)}
      />
      <TextField
        variant="filled"
        label="IP-adress (om fast)"
        fullWidth
        hidden={!showAllFields && !ipAddress}
        value={ipAddress}
        onChange={(e) => setIpAddress(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Användarnamn"
        fullWidth
        hidden={!showAllFields && !username}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Lösenord"
        fullWidth
        hidden={!showAllFields && !password}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Asset-nummer"
        fullWidth
        hidden={!showAllFields && !assetNumber}
        value={assetNumber}
        onChange={(e) => setAssetNumber(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Firmware-version"
        fullWidth
        hidden={!showAllFields && !firmware}
        value={firmware}
        onChange={(e) => setFirmware(e.target.value)}
      />
      <TextField
        variant="filled"
        label="Övrigt"
        fullWidth
        hidden={!showAllFields && !other}
        value={other}
        onChange={(e) => setOther(e.target.value)}
      />
      <FormControlLabel
        label="DOA"
        labelPlacement="start"
        sx={{ ml: 0 }}
        control={
          <Checkbox
            checked={doa}
            onChange={(e) => {
              setDoa(e.target.checked);
            }}
          />
        }
      />

      <Button
        hidden={isNoFieldEmpty}
        startIcon={showAllFields ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setShowAllFields(!showAllFields)}
        sx={{ mt: -1, mb: -2 }}
      >
        {showAllFields ? "Dölj tomma fält" : "Visa fler fält"}
      </Button>
    </LargeDialog>
  );
};

export default EditOrderSerialDialog;
